import { errorMessage } from "../api/client";
import { useLeads, useUpdateLead } from "../api/leads";
import type { LeadRead } from "../api/types";
import { formatDate } from "../lib/format";
import SensitiveToggle, { useSensitiveMask } from "../components/SensitiveToggle";
import { maskEmail, maskName } from "../lib/mask";

function LeadRow({ lead, masked }: { lead: LeadRead; masked: boolean }) {
  const update = useUpdateLead(lead.id);
  const invited = lead.invited_at != null;

  return (
    <tr>
      <td>{masked ? maskName(lead.name) : lead.name}</td>
      <td>{masked ? maskEmail(lead.email) : lead.email}</td>
      <td className="muted small">{lead.source ?? "—"}</td>
      <td className="muted small">{formatDate(lead.created_at)}</td>
      <td>
        {invited ? (
          <span className="muted small">Invited {formatDate(lead.invited_at)}</span>
        ) : (
          <span className="muted small">Waiting</span>
        )}
      </td>
      <td>
        {/* marking is bookkeeping only: the person is still contacted by hand */}
        <button
          className="btn ghost small"
          disabled={update.isPending}
          onClick={() => update.mutate({ invited: !invited })}
        >
          {invited ? "Undo invite" : "Mark invited"}
        </button>
        {update.isError && <span className="error small"> {errorMessage(update.error)}</span>}
      </td>
    </tr>
  );
}

export default function LeadsPage() {
  const { data: leads, isLoading, error } = useLeads();
  const waiting = leads?.filter((l) => l.invited_at == null).length ?? 0;
  const mask = useSensitiveMask();

  return (
    <div className="leads-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Leads</h1>
          <p className="page-sub">
            People who asked to be contacted from the app's waitlist form. Reach out to them,
            then mark the lead invited so nobody is contacted twice.
          </p>
        </div>
      </div>

      <section className="card">
        <div className="card-head">
          <h3>All leads</h3>
          <div className="card-head-tools">
            {leads && leads.length > 0 && (
              <span className="muted small">
                {waiting} waiting · {leads.length} total
              </span>
            )}
            <SensitiveToggle mask={mask} />
          </div>
        </div>
        {isLoading && <p className="muted small">Loading…</p>}
        {error && <p className="error">Failed to load: {errorMessage(error)}</p>}
        {leads && leads.length === 0 && <p className="muted small">No leads yet.</p>}
        {leads && leads.length > 0 && (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Source</th>
                  <th>Requested</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <LeadRow key={l.id} lead={l} masked={mask.masked} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
